import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Search, Clock } from 'lucide-react';
import { MediaLightbox } from "@/components/ui/media-lightbox";
import { useRoleFlags } from '@/auth/useRoleFlags';
import { mediaUrl } from '@/utils/mediaUrl';

// Sabit faaliyet tipleri — filtrede her zaman tam liste gösterilir
// (veride henüz hiç örneği olmasa bile). ActivityManagementTab ile aynı sıra.
const ACTIVITY_TYPES = [
  "Çim Biçimi",
  "Form ve Budama",
  "Bölge Geneli Mobil Temizlik",
  "Diğer"
];

export default function ActivityReport({ personnelId, userRole, parks, personnel, API }) {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  const [loading, setLoading] = useState(false);
  const [allPlans, setAllPlans] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [filterPark, setFilterPark] = useState("all");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [filterFormen, setFilterFormen] = useState("all");
  const [filterActivityType, setFilterActivityType] = useState("all");
  const [lightbox, setLightbox] = useState({ open: false, items: [], index: 0 });

  useEffect(() => {
    loadAllPlans();
  }, []);

  useEffect(() => {
    if (hasSearched) {
      applyFilters();
    }
  }, [allPlans, hasSearched]);

  const visibleFormens = useMemo(() => {
    let formenList = [];
    if (isFormen) {
      formenList = personnel.filter(p => p.id === personnelId);
    } else if (isBolgeMuhendisi) {
      formenList = personnel.filter(
        p => p.role === "formen" && p.bolge_muhendisi_id === personnelId
      );
    } else if (isMudurYardimcisi) {
      const myBolgeMuhendisleri = personnel.filter(
        p => p.role === "bolge_muhendisi" && p.mudur_yardimcisi_id === personnelId
      );
      const bolgeMuhIds = myBolgeMuhendisleri.map(b => b.id);
      formenList = personnel.filter(
        p => p.role === "formen" && bolgeMuhIds.includes(p.bolge_muhendisi_id)
      );
    } else {
      formenList = personnel.filter(p => p.role === "formen");
    }

    return formenList
      .filter(f => f?.id && f?.name)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr-TR", { sensitivity: "base" }));
  }, [personnel, personnelId, userRole]);

  const parkOptions = useMemo(() => {
    const selectedPlans = filterFormen === "all"
      ? allPlans
      : allPlans.filter(p => p.created_by_id === filterFormen);

    const activityParkMap = new Map();
    selectedPlans.forEach((plan) => {
      (plan.activities || []).forEach((activity) => {
        if (activity?.park_id && activity?.park_name) {
          activityParkMap.set(activity.park_id, {
            id: activity.park_id,
            name: activity.park_name
          });
        }
      });
    });

    return Array.from(activityParkMap.values())
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr-TR", { sensitivity: "base" }));
  }, [allPlans, filterFormen]);

  useEffect(() => {
    if (filterPark !== "all" && !parkOptions.some(p => p.id === filterPark)) {
      setFilterPark("all");
    }
  }, [filterPark, parkOptions]);

  const activityTypeOptions = useMemo(() => {
    // Standart 4 faaliyet tipi her zaman görünür (sabit sıra). Veride bunların
    // dışında bir tip varsa (eski kayıtlar) listenin sonuna eklenir.
    const extras = new Set();
    allPlans.forEach((plan) => {
      (plan.activities || []).forEach((activity) => {
        const t = activity?.activity_type;
        if (t && !ACTIVITY_TYPES.includes(t)) {
          extras.add(t);
        }
      });
    });

    return [
      ...ACTIVITY_TYPES,
      ...Array.from(extras).sort((a, b) => a.localeCompare(b, "tr-TR", { sensitivity: "base" })),
    ];
  }, [allPlans]);

  useEffect(() => {
    if (filterActivityType !== "all" && !activityTypeOptions.includes(filterActivityType)) {
      setFilterActivityType("all");
    }
  }, [filterActivityType, activityTypeOptions]);

  const loadAllPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/activity-plans?status=approved`);
      if (response.data.success) {
        let plans = response.data.plans;
        
        // Rol bazlı filtreleme
        if (isFormen) {
          // Formen sadece kendi planlarını görür
          plans = plans.filter(p => p.created_by_id === personnelId);
        } else if (isBolgeMuhendisi) {
          // Bölge Mühendisi altındaki formenlerin planlarını görür
          const myFormens = personnel.filter(p => 
            p.role === "formen" && p.bolge_muhendisi_id === personnelId
          );
          const formenIds = myFormens.map(f => f.id);
          plans = plans.filter(p => formenIds.includes(p.created_by_id));
        } else if (isMudurYardimcisi) {
          // Müdür Yardımcısı altındaki bölge mühendislerinin formenlerin planlarını görür
          const myBolgeMuhendisleri = personnel.filter(p => 
            p.role === "bolge_muhendisi" && p.mudur_yardimcisi_id === personnelId
          );
          const bolgeMuhIds = myBolgeMuhendisleri.map(b => b.id);
          const formens = personnel.filter(p => 
            p.role === "formen" && bolgeMuhIds.includes(p.bolge_muhendisi_id)
          );
          const formenIds = formens.map(f => f.id);
          plans = plans.filter(p => formenIds.includes(p.created_by_id));
        }
        // admin tüm planları görür (filtreleme yok)
        
        setAllPlans(plans);
      }
    } catch (error) {
      console.error("Planları yükleme hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let activities = [];

    // Tüm planlardan faaliyetleri çıkar
    allPlans.forEach(plan => {
      if (plan.activities && Array.isArray(plan.activities)) {
        plan.activities.forEach(activity => {
          activities.push({
            ...activity,
            plan_id: plan.id,
            plan_creator: plan.created_by_name,
            formen_id: plan.created_by_id,
            week_start: plan.week_start_date,
            week_end: plan.week_end_date
          });
        });
      }
    });

    // Filtreleri uygula
    if (filterPark && filterPark !== "all") {
      activities = activities.filter(a => a.park_id === filterPark);
    }

    if (filterStartDate) {
      activities = activities.filter(a => a.date && a.date >= filterStartDate);
    }

    if (filterEndDate) {
      activities = activities.filter(a => a.date && a.date <= filterEndDate);
    }

    if (filterFormen && filterFormen !== "all") {
      activities = activities.filter(a => a.formen_id === filterFormen);
    }

    if (filterActivityType && filterActivityType !== "all") {
      activities = activities.filter(a => a.activity_type === filterActivityType);
    }

    // Tarihe göre sırala (en yeni önce)
    activities.sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : new Date(0);
      const dateB = b.date ? new Date(b.date) : new Date(0);
      return dateB - dateA;
    });

    setFilteredActivities(activities);
  };

  const resetFilters = () => {
    setFilterPark("all");
    setFilterStartDate("");
    setFilterEndDate("");
    setFilterFormen("all");
    setFilterActivityType("all");
    setFilteredActivities([]);
    setHasSearched(false);
  };

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle>Faaliyet Raporu</CardTitle>
        <CardDescription>
          Hangi gün, hangi ekip, nerede, ne yaptı - Tüm onaylanmış faaliyetler
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Filtreler — dar ekranda kademeli kırılır, dropdownlar sıkışmaz */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <div>
            <Label className="text-xs">Park Filtrele</Label>
            <SearchableSelect
              value={filterPark}
              onChange={setFilterPark}
              className="mt-1"
              allLabel="Tüm Parklar"
              allValue="all"
              searchPlaceholder="Park ara..."
              options={parkOptions.map(park => ({ id: park.id, name: park.name }))}
            />
          </div>

          <div>
            <Label className="text-xs">Faaliyet Filtrele</Label>
            <SearchableSelect
              value={filterActivityType}
              onChange={setFilterActivityType}
              className="mt-1"
              allLabel="Tüm Faaliyetler"
              allValue="all"
              searchPlaceholder="Faaliyet ara..."
              options={activityTypeOptions.map(type => ({ id: type, name: type }))}
            />
          </div>

          <div className="col-span-2">
            <Label className="text-xs">Tarih Aralığı</Label>
            <DateRangePicker
              startDate={filterStartDate}
              endDate={filterEndDate}
              onChange={({ start, end }) => {
                setFilterStartDate(start);
                setFilterEndDate(end);
              }}
              placeholder="Tarih aralığı"
              manualInput
            />
          </div>

          <div>
            <Label className="text-xs">Formen Filtrele</Label>
            <SearchableSelect
              value={filterFormen}
              onChange={setFilterFormen}
              className="mt-1"
              allLabel="Tüm Formenler"
              allValue="all"
              searchPlaceholder="Formen ara..."
              options={visibleFormens.map(formen => ({ id: formen.id, name: formen.name }))}
            />
          </div>

          <div className="flex items-end">
            <Button
              type="button"
              className="w-full"
              onClick={() => {
                applyFilters();
                setHasSearched(true);
              }}
            >
              <Search className="h-4 w-4 mr-2" />
              Ara
            </Button>
          </div>

          <div className="flex items-end">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={resetFilters}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Filtreleri Temizle
            </Button>
          </div>
        </div>

        {/* Rapor Tablosu */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        ) : !hasSearched ? (
          <div className="text-center py-12 text-gray-500">
            <Search className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">Filtreleri seçip Ara butonuna basın</p>
            <p className="text-sm">Veriler sadece Ara ile listelenir</p>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Search className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">Faaliyet bulunamadı</p>
            <p className="text-sm">Filtreleri değiştirmeyi deneyin</p>
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-gray-600">
              Toplam <span className="font-bold">{filteredActivities.length}</span> faaliyet bulundu
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tarih</TableHead>
                  <TableHead>Gün</TableHead>
                  <TableHead>Ekip/Formen</TableHead>
                  <TableHead>Faaliyet</TableHead>
                  <TableHead>Park</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Not</TableHead>
                  <TableHead>Medya</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredActivities.map((activity, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">
                      {activity.date ? new Date(activity.date).toLocaleDateString('tr-TR') : '-'}
                    </TableCell>
                    <TableCell>{activity.day_name || '-'}</TableCell>
                    <TableCell>{activity.plan_creator || 'Bilinmiyor'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-50">
                        {activity.activity_type || 'Belirtilmemiş'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {activity.park_name || (
                        <span className="text-gray-400">Park Belirtilmemiş</span>
                      )}
                      {(() => {
                        let a = activity.area, g = activity.grass_area;
                        if (a == null && g == null && activity.park_id) {
                          const p = (parks || []).find(x => x.id === activity.park_id);
                          a = p?.area ?? null; g = p?.grass_area ?? null;
                        }
                        const parts = [];
                        if (a != null) parts.push(`Toplam ${a} m²`);
                        if (g != null) parts.push(`Çim ${g} m²`);
                        return parts.length ? (
                          <div className="text-[11px] text-emerald-700 mt-0.5">📐 {parts.join(" · ")}</div>
                        ) : null;
                      })()}
                    </TableCell>
                    <TableCell>
                      {activity.completed_at ? (
                        activity.is_completed ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="text-xs">Tamamlandı</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-red-600">
                            <XCircle className="h-4 w-4" />
                            <span className="text-xs">Tamamlanamadı</span>
                          </div>
                        )
                      ) : (
                        <div className="flex items-center gap-1 text-amber-600">
                          <Clock className="h-4 w-4" />
                          <span className="text-xs">Bekliyor</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {activity.notes || activity.completion_notes || "-"}
                    </TableCell>
                    <TableCell>
                      {Array.isArray(activity.media) && activity.media.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {activity.media.map((m, mi) => (
                            <button
                              key={mi}
                              type="button"
                              onClick={() => setLightbox({ open: true, items: activity.media, index: mi })}
                              className="relative block w-10 h-10 rounded border overflow-hidden bg-black"
                              title={m.type === "video" ? "Videoyu büyüt" : "Fotoğrafı büyüt"}
                            >
                              {m.type === "photo" ? (
                                <img src={mediaUrl(API, m.url)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <>
                                  <video src={mediaUrl(API, m.url)} className="w-full h-full object-cover" muted />
                                  <span className="absolute inset-0 flex items-center justify-center text-white text-sm">▶</span>
                                </>
                              )}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xs">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
    {lightbox.open && (
      <MediaLightbox
        items={lightbox.items}
        index={lightbox.index}
        apiBase={API}
        onClose={() => setLightbox({ open: false, items: [], index: 0 })}
        onNavigate={(i) => setLightbox(prev => ({ ...prev, index: i }))}
      />
    )}
    </>
  );
}
